import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Check, LogOut, User, Package, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { lookupPincode } from "@/lib/pincode";
import { useSeo } from "@/lib/seo";
import { useStore } from "@/context/StoreContext";
import { Reveal } from "@/components/Reveal";

const EMPTY_ADDR = { address_type: "home", name: "", mobile: "", line1: "", line2: "", landmark: "", pincode: "", city: "", state: "" };

export default function AccountPage() {
  useSeo({ title: "My Account | Soonyoung", noindex: true, path: "/account" });
  const { isAuthed, authReady, setLoginOpen, customer, logout } = useStore();
  const [profile, setProfile] = useState({ name: "", email: "", dob: "", gender: "", accepts_marketing: true });
  const [addresses, setAddresses] = useState([]);
  const [form, setForm] = useState(EMPTY_ADDR);
  const [showForm, setShowForm] = useState(false);
  const [areas, setAreas] = useState([]);

  const onPincode = async (pin) => {
    setForm((f) => ({ ...f, pincode: pin }));
    if (!/^\d{6}$/.test(pin)) { setAreas([]); return; }
    try {
      const info = await lookupPincode(pin);
      setAreas(info.areas);
      setForm((f) => ({
        ...f,
        city: info.district,
        state: info.state,
        line2: info.areas.length === 1 ? info.areas[0].name : f.line2,
      }));
    } catch { setAreas([]); toast.error("Pincode not found — enter city and state manually"); }
  };

  const loadAddresses = useCallback(() => {
    api.get("/ecom/addresses").then((r) => setAddresses(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (customer) setProfile({
      name: customer.name || "", email: customer.email || "", dob: customer.dob || "",
      gender: customer.gender || "", accepts_marketing: customer.accepts_marketing ?? true,
    });
    if (isAuthed) loadAddresses();
  }, [customer, isAuthed, loadAddresses]);

  if (authReady && !isAuthed) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-7xl flex-col items-center justify-center px-4 pt-28 text-center" data-testid="account-login-prompt">
        <User className="h-10 w-10 text-gold" />
        <h1 className="mt-4 font-display text-3xl text-cream">Your account lives here</h1>
        <button data-testid="account-login-btn" onClick={() => setLoginOpen(true)} className="mt-6 rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest">Sign in with OTP</button>
      </div>
    );
  }

  const saveProfile = async () => {
    try {
      await api.patch("/ecom/auth/me", {
        name: profile.name || undefined, email: profile.email || undefined,
        dob: profile.dob || undefined, gender: profile.gender || undefined,
        accepts_marketing: profile.accepts_marketing,
      });
      toast.success("Profile updated");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveAddress = async () => {
    if (!form.name || !form.mobile || !form.line1 || !form.pincode) { toast.error("Fill name, mobile, address and pincode"); return; }
    try {
      await api.post("/ecom/addresses", { ...form, line2: form.line2 || null, landmark: form.landmark || null, city: form.city || null, state: form.state || null, label: null });
      toast.success("Address added");
      setForm(EMPTY_ADDR); setShowForm(false); loadAddresses();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const field = "rounded-xl border border-champagne/50 bg-forest px-4 py-3 text-sm text-cream placeholder:text-fern focus:border-gold focus:outline-none";

  return (
    <section data-testid="account-page" className="mx-auto max-w-5xl px-4 pb-20 pt-28 sm:px-6 sm:pt-32">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">My Account</p>
            <h1 className="mt-2 font-display text-3xl tracking-tight text-cream sm:text-5xl">
              {customer?.name || "Beautiful"} <span className="italic text-gold">· +91 {customer?.mobile}</span>
            </h1>
          </div>
          <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-2 rounded-full border border-champagne/50 px-5 py-2.5 font-mono text-[10px] uppercase tracking-widest text-sage hover:border-gold hover:text-gold">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </Reveal>

      <Reveal delay={0.05}>
        <Link
          to="/orders"
          data-testid="account-orders-link"
          className="group mt-8 flex items-center justify-between rounded-3xl border border-champagne/40 bg-pine p-6 transition-colors hover:border-gold"
        >
          <div className="flex items-center gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gold/15 text-gold">
              <Package className="h-5 w-5" />
            </span>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">My Orders</p>
              <p className="mt-1 font-display text-xl text-cream">Track, review and reorder your rituals</p>
            </div>
          </div>
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold text-forest transition-transform duration-300 group-hover:translate-x-1">
            <ArrowRight className="h-4 w-4" />
          </span>
        </Link>
      </Reveal>

      <div className="mt-10 grid gap-8 lg:grid-cols-2">
        <div className="rounded-3xl border border-champagne/40 bg-pine p-6" data-testid="profile-card">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Profile</p>
          <div className="mt-4 space-y-4">
            <input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} placeholder="Full name" className={`${field} w-full`} />
            <input data-testid="profile-email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} placeholder="Email" className={`${field} w-full`} />
            <div className="grid grid-cols-2 gap-4">
              <input data-testid="profile-dob" type="date" value={profile.dob} onChange={(e) => setProfile({ ...profile, dob: e.target.value })} className={field} />
              <select data-testid="profile-gender" value={profile.gender} onChange={(e) => setProfile({ ...profile, gender: e.target.value })} className={field}>
                <option value="">Gender</option><option value="female">Female</option><option value="male">Male</option><option value="other">Other</option>
              </select>
            </div>
            <label className="flex items-center gap-3 text-xs text-sage">
              <input data-testid="profile-marketing" type="checkbox" checked={profile.accepts_marketing} onChange={(e) => setProfile({ ...profile, accepts_marketing: e.target.checked })} className="h-4 w-4 accent-[#FF2D87]" />
              Send me drops, offers and rituals
            </label>
            <button data-testid="profile-save-btn" onClick={saveProfile} className="rounded-full bg-gold px-8 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-forest">Save Profile</button>
          </div>
        </div>

        <div className="rounded-3xl border border-champagne/40 bg-pine p-6" data-testid="addresses-card">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Address Book</p>
            <button data-testid="address-add-toggle" onClick={() => setShowForm((s) => !s)} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-gold hover:underline">
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          </div>

          <ul className="mt-4 space-y-3">
            {addresses.map((a) => (
              <li key={a.id} data-testid={`account-address-${a.id}`} className="rounded-2xl border border-champagne/30 p-4">
                <div className="flex items-center justify-between">
                  <p className="font-display text-base text-cream">{a.name} {a.is_default && <span className="ml-1 rounded bg-gold px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-widest text-forest">Default</span>}</p>
                  <div className="flex gap-3">
                    {!a.is_default && (
                      <button data-testid={`address-default-${a.id}`} onClick={async () => { await api.post(`/ecom/addresses/${a.id}/default`); loadAddresses(); }} className="text-fern hover:text-gold" aria-label="Set default"><Check className="h-4 w-4" /></button>
                    )}
                    <button data-testid={`address-delete-${a.id}`} onClick={async () => { await api.delete(`/ecom/addresses/${a.id}`); loadAddresses(); toast.success("Address removed"); }} className="text-fern hover:text-gold" aria-label="Delete address"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <p className="mt-1 text-xs text-sage">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city} {a.pincode}, {a.state}</p>
              </li>
            ))}
            {addresses.length === 0 && !showForm && <p className="py-6 text-center text-xs text-fern">No addresses saved yet</p>}
          </ul>

          {showForm && (
            <div className="mt-4 space-y-3 rounded-2xl border border-champagne/30 p-4" data-testid="account-address-form">
              <input data-testid="acc-addr-name" placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${field} w-full`} />
              <input data-testid="acc-addr-mobile" placeholder="Mobile *" maxLength={10} value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} className={`${field} w-full`} />
              <input data-testid="acc-addr-line1" placeholder="Flat / House / Street *" value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} className={`${field} w-full`} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="acc-addr-pincode" placeholder="Pincode *" maxLength={6} value={form.pincode} onChange={(e) => onPincode(e.target.value.replace(/\D/g, "").slice(0, 6))} className={field} />
                <input data-testid="acc-addr-city" placeholder="District / City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={field} />
              </div>
              {areas.length > 0 && (
                <div>
                  {areas.length > 1 ? (
                    <select
                      data-testid="acc-addr-area-select"
                      value={form.line2}
                      onChange={(e) => setForm({ ...form, line2: e.target.value })}
                      className={`${field} w-full border-gold/50`}
                    >
                      <option value="">Select your area — {areas.length} areas in {form.pincode} *</option>
                      {areas.map((a) => (
                        <option key={a.name} value={a.name}>{a.name}{a.delivery ? "" : " (non-delivery office)"}</option>
                      ))}
                    </select>
                  ) : (
                    <p data-testid="acc-addr-area-single" className="rounded-xl bg-moss px-4 py-3 text-sm text-cream">Area: <b>{areas[0].name}</b></p>
                  )}
                  <p data-testid="acc-addr-area-info" className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-fern">
                    {(areas.find((a) => a.name === form.line2)?.taluk || areas[0].taluk || "—")} · {form.city || areas[0].district} · {form.state || areas[0].state}
                  </p>
                </div>
              )}
              <button data-testid="acc-addr-save-btn" onClick={saveAddress} className="rounded-full bg-gold px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-forest">Save Address</button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
